import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SkillRadarChart, { SkillData } from '../components/visualization/SkillRadarChart';
import SkillBarChart from '../components/visualization/SkillBarChart';

// Types pour les catégories de compétences
interface SkillCategory {
  id: string;
  title: string;
  description: string;
  colorPrimary: string;
  colorSecondary: string;
  skills: SkillData[];
  filter?: (skill: SkillData) => boolean; // Filtre optionnel pour séparer les compétences
  visible?: boolean; // Visibilité de la catégorie
  chartOrder?: number; // Ordre d'affichage
}

// Styles pour la page
const PageContainer = styled.div`
  padding: 8rem 2rem 6rem;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 7rem 1.5rem 5rem;
  }
`;

// Conteneurs pour les vues mobile et desktop
const DesktopSkillsContainer = styled.div`
  @media (max-width: 1400px) {
    display: none;
  }
`;

const MobileSkillsContainer = styled.div`
  display: none;
  
  @media (max-width: 1400px) {
    display: block;
  }
`;

const PageTitle = styled(motion.h2)`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 3rem;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.accent});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  
  @media (max-width: 768px) {
    font-size: 2.3rem;
    margin-bottom: 2rem;
  }
`;

const PageDescription = styled(motion.p)`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 4rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 1.1rem;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 2.5rem;
    padding: 0 0.5rem;
  }
`;

const CategoriesGrid = styled.div`
  display: grid;
  /* Fixé à 2x2 pour une disposition symétrique */
  grid-template-columns: repeat(2, 1fr);
  gap: 2.5rem;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const CategoryCard = styled(motion.div)`
  background-color: ${({ theme }) => `${theme.colors.backgroundLight}cc`};
  border-radius: 16px;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  box-shadow: ${({ theme }) => theme.shadows.md};
  transition: box-shadow 0.3s ease, transform 0.3s ease;
  
  &:hover {
    box-shadow: ${({ theme }) => theme.shadows.lg};
    transform: translateY(-5px);
  }
`;

const CategoryHeader = styled.div<{ color: string }>`
  background: linear-gradient(135deg, ${props => props.color}, ${props => props.color}aa);
  padding: 1.8rem;
  position: relative;
  overflow: hidden;
  
  @media (max-width: 768px) {
    padding: 1.4rem;
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at top right, rgba(255,255,255,0.2), transparent 70%);
    z-index: 1;
  }
`;

const CategoryTitle = styled.h3`
  margin: 0;
  font-size: 2rem;
  color: white;
  position: relative;
  z-index: 2;
  text-shadow: 0 2px 4px rgba(0,0,0,0.2);
  
  @media (max-width: 768px) {
    font-size: 1.7rem;
  }
`;

const CategoryDescription = styled.p`
  color: rgba(255,255,255,0.9);
  margin: 0.8rem 0 0;
  font-size: 1rem;
  position: relative;
  z-index: 2;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin: 0.5rem 0 0;
  }
`;

const ChartContainer = styled.div`
  flex: 1;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 1rem 1rem 1.5rem;
    display: flex;
    align-items: flex-start; /* Aligné en haut plutôt qu'au centre */
    justify-content: flex-start; /* Aligné à gauche plutôt qu'au centre */
  }
`;

// Animations
const fadeUpVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};

// Composant principal SkillsPage
const SkillsPage: React.FC = () => {
  // @ts-ignore
  const { t, i18n } = useTranslation('skills');
  const [isMobile, setIsMobile] = useState(false);
  
  // Détecter si l'écran est de taille mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    // Vérification initiale
    handleResize();
    
    // Mettre à jour lors des changements de taille d'écran
    window.addEventListener('resize', handleResize);
    
    // Nettoyage
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);


  // Données pour les compétences
  const skillCategories: SkillCategory[] = [
    {
      id: 'data-science',
      title: t('categories.dataScience.title'),
      description: t('categories.dataScience.description'),
      colorPrimary: '#9b59b6', // Violet pour Data Science
      colorSecondary: '#8e44ad',
      skills: [
        { 
          name: t('skills.dataScience.machineLearning.name'), 
          value: 80, 
          description: t('skills.dataScience.machineLearning.description')
        },
        { 
          name: t('skills.dataScience.classification.name'), 
          value: 70, 
          description: t('skills.dataScience.classification.description')
        },
        { 
          name: t('skills.dataScience.timeSeries.name'), 
          value: 75, 
          description: t('skills.dataScience.timeSeries.description')
        },
        { 
          name: t('skills.dataScience.gridSearch.name'), 
          value: 60, 
          description: t('skills.dataScience.gridSearch.description')
        },
        { 
          name: t('skills.dataScience.deepLearning.name'), 
          value: 80, 
          description: t('skills.dataScience.deepLearning.description')
        },
        { 
          name: t('skills.dataScience.nlp.name'), 
          value: 80, 
          description: t('skills.dataScience.nlp.description')
        },
        { 
          name: t('skills.dataScience.bigData.name'), 
          value: 35, 
          description: t('skills.dataScience.bigData.description')
        }
      ],
      visible: true,
      chartOrder: 1
    },
    {
      id: 'statistiques',
      title: t('categories.statistics.title'),
      description: t('categories.statistics.description'),
      colorPrimary: '#3949ab', // Bleu indigo pour les Statistiques (très différent du violet)
      colorSecondary: '#283593',
      skills: [
        { 
          name: t('skills.statistics.regression.name'), 
          value: 65, 
          description: t('skills.statistics.regression.description')
        },
        { 
          name: t('skills.statistics.analysis.name'), 
          value: 70, 
          description: t('skills.statistics.analysis.description')
        },
        { 
          name: t('skills.statistics.pca.name'), 
          value: 55, 
          description: t('skills.statistics.pca.description')
        },
        { 
          name: t('skills.statistics.bayesian.name'), 
          value: 40, 
          description: t('skills.statistics.bayesian.description')
        },
        { 
          name: t('skills.statistics.multivariate.name'), 
          value: 30, 
          description: t('skills.statistics.multivariate.description')
        },
        { 
          name: t('skills.statistics.nonparametric.name'), 
          value: 45, 
          description: t('skills.statistics.nonparametric.description')
        }
      ],
      visible: true,
      chartOrder: 7
    },
    {
      id: 'programming-languages',
      title: t('categories.programmingLanguages.title'),
      description: t('categories.programmingLanguages.description'),
      colorPrimary: '#3498db', // Bleu pour les langages de programmation
      colorSecondary: '#2980b9',
      skills: [
        { 
          name: t('skills.programming.python.name'), 
          value: 80, 
          description: t('skills.programming.python.description')
        },
        { 
          name: t('skills.programming.r.name'), 
          value: 35, 
          description: t('skills.programming.r.description')
        },
        { 
          name: t('skills.programming.javascript.name'), 
          value: 35, 
          description: t('skills.programming.javascript.description')
        },
        { 
          name: t('skills.programming.typescript.name'), 
          value: 50, 
          description: t('skills.programming.typescript.description')
        },
        { 
          name: t('skills.programming.sql.name'), 
          value: 80, 
          description: t('skills.programming.sql.description')
        }
      ],
      chartOrder: 3
    },
    {
      id: 'development-tools',
      title: t('categories.developmentTools.title'),
      description: t('categories.developmentTools.description'),
      colorPrimary: '#00bcd4', // Turquoise/Cyan pour les outils de développement
      colorSecondary: '#0097a7',
      skills: [
        { 
          name: t('skills.programming.git.name'), 
          value: 70, 
          description: t('skills.programming.git.description')
        },
        { 
          name: t('skills.programming.docker.name'), 
          value: 70, 
          description: t('skills.programming.docker.description')
        },
        { 
          name: t('skills.programming.vscode.name'), 
          value: 75, 
          description: t('skills.programming.vscode.description')
        },
        { 
          name: t('skills.programming.vmware.name'), 
          value: 55, 
          description: t('skills.programming.vmware.description')
        },
        { 
          name: t('skills.programming.n8n.name'), 
          value: 15, 
          description: t('skills.programming.n8n.description')
        },
        { 
          name: t('skills.programming.powerautomate.name'), 
          value: 75, 
          description: t('skills.programming.powerautomate.description')
        }
      ],
      chartOrder: 2
    },
    {
      id: 'visualisation-bi',
      title: t('categories.visualization.title'),
      description: t('categories.visualization.description'),
      colorPrimary: '#1abc9c',
      colorSecondary: '#16a085',
      skills: [
        { 
          name: t('skills.visualization.powerbi.name'), 
          value: 80, 
          description: t('skills.visualization.powerbi.description')
        },
        { 
          name: t('skills.visualization.d3.name'), 
          value: 60, 
          description: t('skills.visualization.d3.description')
        },
        { 
          name: t('skills.visualization.matplotlib.name'), 
          value: 80, 
          description: t('skills.visualization.matplotlib.description')
        },
        { 
          name: t('skills.visualization.streamlit.name'), 
          value: 60, 
          description: t('skills.visualization.streamlit.description')
        },
        { 
          name: t('skills.visualization.tableau.name'), 
          value: 15, 
          description: t('skills.visualization.tableau.description')
        },
        { 
          name: t('skills.programming.rshiny.name'), 
          value: 30, 
          description: t('skills.programming.rshiny.description')
        }
      ],
      chartOrder : 6
    },
    {
      id: 'ecology',
      title: t('categories.ecology.title'),
      description: t('categories.ecology.description'),
      colorPrimary: '#2ecc71',
      colorSecondary: '#27ae60',
      skills: [
        { 
          name: t('skills.ecology.communities.name'), 
          value: 60, 
          description: t('skills.ecology.communities.description')
        },
        { 
          name: t('skills.ecology.factors.name'), 
          value: 40, 
          description: t('skills.ecology.factors.description')
        },
        { 
          name: t('skills.ecology.gis.name'), 
          value: 60, 
          description: t('skills.ecology.gis.description')
        },
        { 
          name: t('skills.ecology.dataInterpretation.name'), 
          value: 80, 
          description: t('skills.ecology.dataInterpretation.description')
        },
        { 
          name: t('skills.ecology.biostatistics.name'), 
          value: 60, 
          description: t('skills.ecology.biostatistics.description')
        },
        { 
          name: t('skills.ecology.numericalAnalysis.name'), 
          value: 60, 
          description: t('skills.ecology.numericalAnalysis.description')
        },
        { 
          name: t('skills.ecology.labSkills.name'), 
          value: 40, 
          description: t('skills.ecology.labSkills.description')
        }
      ],
      chartOrder: 8
    },
    {
      id: 'management',
      title: t('categories.methodology.title'),
      description: t('categories.methodology.description'),
      colorPrimary: '#f39c12',
      colorSecondary: '#e74c3c',
      skills: [
        { 
          name: t('skills.methodology.projectManagement.name'), 
          value: 75, 
          description: t('skills.methodology.projectManagement.description')
        },
        { 
          name: t('skills.methodology.requirements.name'), 
          value: 75, 
          description: t('skills.methodology.requirements.description')
        },
        { 
          name: t('skills.methodology.documentation.name'), 
          value: 80, 
          description: t('skills.methodology.documentation.description')
        },
        { 
          name: t('skills.methodology.communication.name'), 
          value: 80, 
          description: t('skills.methodology.communication.description')
        },
        { 
          name: t('skills.methodology.collaboration.name'), 
          value: 75, 
          description: t('skills.methodology.collaboration.description')
        },
        { 
          name: t('skills.methodology.presentation.name'), 
          value: 80, 
          description: t('skills.methodology.presentation.description')
        }
      ],
      chartOrder: 4
    },
    {
      id: 'soft-skills',
      title: t('categories.softSkills.title'),
      description: t('categories.softSkills.description'),
      colorPrimary: '#95a5a6', // Gris neutre pour refléter le côté transversal
      colorSecondary: '#7f8c8d',
      skills: [
        { 
          name: t('skills.softSkills.analytical.name'), 
          value: 80, 
          description: t('skills.softSkills.analytical.description')
        },
        { 
          name: t('skills.softSkills.curiosity.name'), 
          value: 75, 
          description: t('skills.softSkills.curiosity.description')
        },
        { 
          name: t('skills.softSkills.autonomy.name'), 
          value: 80, 
          description: t('skills.softSkills.autonomy.description')
        },
        { 
          name: t('skills.softSkills.adaptability.name'), 
          value: 97, 
          description: t('skills.softSkills.adaptability.description')
        },
        { 
          name: t('skills.softSkills.criticalThinking.name'), 
          value: 80, 
          description: t('skills.softSkills.criticalThinking.description')
        }
      ],
      chartOrder: 5
    }
    
  ];
  

  return (
    <PageContainer>
      <PageTitle
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        {t('pageTitle')}
      </PageTitle>
      
      <PageDescription
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {t('pageDescription')}
      </PageDescription>
      
      {/* Version desktop - Grille de cartes avec graphiques radar */}
      <DesktopSkillsContainer>
        <CategoriesGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {skillCategories
            .filter(category => category.visible !== false)
            .sort((a, b) => (a.chartOrder || 999) - (b.chartOrder || 999))
            .map((category) => (
              <CategoryCard 
                key={category.id}
                variants={fadeUpVariants}
                transition={{ duration: 0.5 }}
              >
                <CategoryHeader color={category.colorPrimary}>
                  <CategoryTitle>{category.title}</CategoryTitle>
                  <CategoryDescription>{category.description}</CategoryDescription>
                </CategoryHeader>
                
                <ChartContainer>
                  <SkillRadarChart
                    skills={category.filter ? category.skills.filter(category.filter) : category.skills}
                    id={`radar-${category.id}`}
                    colors={{
                      area: `${category.colorPrimary}99`,
                      point: category.colorPrimary,
                      grid: 'rgba(255, 255, 255, 0.2)',
                      text: '#ffffff'
                    }}
                    animationDuration={1200}
                    showLegend={false}
                    filter={category.filter}
                  />
                </ChartContainer>
              </CategoryCard>
            ))}
        </CategoriesGrid>
      </DesktopSkillsContainer>
      
      {/* Version mobile - Liste verticale avec graphiques à barres */}
      <MobileSkillsContainer>
        <CategoriesGrid
          as={motion.div}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {skillCategories
            .filter(category => category.visible !== false)
            .sort((a, b) => (a.chartOrder || 999) - (b.chartOrder || 999))
            .map((category) => (
              <CategoryCard 
                key={category.id}
                variants={fadeUpVariants}
                transition={{ duration: 0.5 }}
              >
                <CategoryHeader color={category.colorPrimary}>
                  <CategoryTitle>{category.title}</CategoryTitle>
                  <CategoryDescription>{category.description}</CategoryDescription>
                </CategoryHeader>
                
                <ChartContainer>
                  <SkillBarChart
                    skills={category.filter ? category.skills.filter(category.filter) : category.skills}
                    id={`bar-${category.id}`}
                    colors={{
                      primary: category.colorPrimary,
                      secondary: category.colorSecondary,
                      text: '#ffffff'
                    }}
                    animationDuration={800}
                    showLegend={false}
                    filter={category.filter}
                  />
                </ChartContainer>
              </CategoryCard>
            ))}
        </CategoriesGrid>
      </MobileSkillsContainer>
    </PageContainer>
  );
};

export default SkillsPage;